import { executeOps } from "./bridge/office-ops.ts";
import { log } from "./log.ts";
import { parseDeck } from "./pptx.ts";

// Per-message deck snapshots so a user can revert the live deck back to its
// state immediately before a given chat message. Snapshots live in IndexedDB
// (multi-MB blobs are fine there; localStorage isn't) and are keyed by chat
// message id. .pptx is already a zip — no extra compression here, it would
// burn CPU for ~5% size win.

const DB_NAME = "bigpp.snapshots";
const STORE = "snapshots";
const DB_VERSION = 1;
const MAX_SNAPSHOTS = 50;

type SnapshotRow = {
  messageId: string;
  bytes: Uint8Array;
  capturedAt: number;
};

// When a save's bytes match the latest stored snapshot, we write a
// zero-byte alias row that points at the real one. Revert follows the
// alias so every user message stays revertable, but we avoid duplicating
// multi-MB blobs across the store.
type SnapshotAliasRow = {
  messageId: string;
  aliasOf: string;
  capturedAt: number;
};

const isAliasRow = (v: unknown): v is SnapshotAliasRow => {
  if (typeof v !== "object" || v === null) return false;
  let id: string | null = null;
  let alias: string | null = null;
  let at: number | null = null;
  for (const [k, val] of Object.entries(v)) {
    if (k === "messageId" && typeof val === "string") id = val;
    if (k === "aliasOf" && typeof val === "string") alias = val;
    if (k === "capturedAt" && typeof val === "number") at = val;
  }
  return id !== null && alias !== null && at !== null;
};

const openDb = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "messageId" });
      }
    };
    req.onsuccess = () => {
      resolve(req.result);
    };
    req.onerror = () => {
      reject(req.error ?? new Error("IndexedDB open failed"));
    };
  });

const txDone = (tx: IDBTransaction): Promise<void> =>
  new Promise((resolve, reject) => {
    tx.oncomplete = () => {
      resolve();
    };
    tx.onerror = () => {
      reject(tx.error ?? new Error("IndexedDB transaction failed"));
    };
    tx.onabort = () => {
      reject(tx.error ?? new Error("IndexedDB transaction aborted"));
    };
  });

const reqDone = <T>(req: IDBRequest<T>): Promise<T> =>
  new Promise((resolve, reject) => {
    req.onsuccess = () => {
      resolve(req.result);
    };
    req.onerror = () => {
      reject(req.error ?? new Error("IndexedDB request failed"));
    };
  });

const bytesEqual = (a: Uint8Array, b: Uint8Array): boolean => {
  if (a.byteLength !== b.byteLength) return false;
  for (let i = 0; i < a.byteLength; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

// Returns the message ids of snapshots evicted to stay under MAX_SNAPSHOTS,
// so the caller can clear their UI revert affordances in lockstep. If the
// deck bytes match the most recent snapshot, no row is written and
// `deduped: true` is returned — reverting to the current message would be
// identical to reverting to the previous one, so the UI can hide the button.
export const saveSnapshot = async (
  messageId: string,
  bytes: Uint8Array,
  capturedAt: number,
): Promise<{ evictedIds: string[]; deduped: boolean }> => {
  const startedAt = Date.now();
  const db = await openDb();
  try {
    {
      const tx = db.transaction(STORE, "readonly");
      const store = tx.objectStore(STORE);
      const rows: SnapshotRow[] = [];
      await new Promise<void>((resolve, reject) => {
        const cursorReq = store.openCursor();
        cursorReq.onerror = () => {
          reject(cursorReq.error ?? new Error("cursor failed"));
        };
        cursorReq.onsuccess = () => {
          const cursor = cursorReq.result;
          if (!cursor) {
            resolve();
            return;
          }
          const v: unknown = cursor.value;
          if (typeof v === "object" && v !== null) {
            let id: string | null = null;
            let at: number | null = null;
            let b: Uint8Array | null = null;
            for (const [k, val] of Object.entries(v)) {
              if (k === "messageId" && typeof val === "string") id = val;
              if (k === "capturedAt" && typeof val === "number") at = val;
              if (k === "bytes" && val instanceof Uint8Array) b = val;
            }
            if (id !== null && at !== null && b !== null) {
              rows.push({ messageId: id, capturedAt: at, bytes: b });
            }
          }
          cursor.continue();
        };
      });
      rows.sort((a, b) => b.capturedAt - a.capturedAt);
      const latestRow = rows[0];
      if (latestRow && bytesEqual(latestRow.bytes, bytes)) {
        const aliasTx = db.transaction(STORE, "readwrite");
        const aliasStore = aliasTx.objectStore(STORE);
        const alias: SnapshotAliasRow = {
          messageId,
          aliasOf: latestRow.messageId,
          capturedAt,
        };
        aliasStore.put(alias);
        await txDone(aliasTx);
        log.debug("addin.snapshot.captured", {
          messageId,
          bytes: bytes.byteLength,
          durationMs: Date.now() - startedAt,
        });
        return { evictedIds: [], deduped: true };
      }
    }

    const tx = db.transaction(STORE, "readwrite");
    const store = tx.objectStore(STORE);
    const row: SnapshotRow = { messageId, bytes, capturedAt };
    store.put(row);

    // Cap retention at MAX_SNAPSHOTS. Evicting a real snapshot that aliases
    // point at would strand the aliases, so when we need to drop a real row
    // with dependents, promote the newest alias to hold the bytes and
    // redirect the rest at it.
    type RowMeta =
      | { kind: "real"; messageId: string; capturedAt: number; bytes: Uint8Array }
      | { kind: "alias"; messageId: string; capturedAt: number; aliasOf: string };
    const rows: RowMeta[] = [];
    await new Promise<void>((resolve, reject) => {
      const cursorReq = store.openCursor();
      cursorReq.onerror = () => {
        reject(cursorReq.error ?? new Error("cursor failed"));
      };
      cursorReq.onsuccess = () => {
        const cursor = cursorReq.result;
        if (!cursor) {
          resolve();
          return;
        }
        const v: unknown = cursor.value;
        if (isAliasRow(v)) {
          rows.push({ kind: "alias", messageId: v.messageId, capturedAt: v.capturedAt, aliasOf: v.aliasOf });
        } else if (typeof v === "object" && v !== null) {
          let id: string | null = null;
          let at: number | null = null;
          let b: Uint8Array | null = null;
          for (const [k, val] of Object.entries(v)) {
            if (k === "messageId" && typeof val === "string") id = val;
            if (k === "capturedAt" && typeof val === "number") at = val;
            if (k === "bytes" && val instanceof Uint8Array) b = val;
          }
          if (id !== null && at !== null && b !== null) {
            rows.push({ kind: "real", messageId: id, capturedAt: at, bytes: b });
          }
        }
        cursor.continue();
      };
    });

    const evictedIds: string[] = [];
    if (rows.length > MAX_SNAPSHOTS) {
      rows.sort((a, b) => a.capturedAt - b.capturedAt);
      const toDrop = rows.slice(0, rows.length - MAX_SNAPSHOTS);
      const dropSet = new Set(toDrop.map((r) => r.messageId));
      for (const d of toDrop) {
        if (d.kind === "alias") {
          store.delete(d.messageId);
          evictedIds.push(d.messageId);
          continue;
        }
        const dependents = rows.filter(
          (r) => r.kind === "alias" && r.aliasOf === d.messageId && !dropSet.has(r.messageId),
        );
        if (dependents.length === 0) {
          store.delete(d.messageId);
          evictedIds.push(d.messageId);
          continue;
        }
        dependents.sort((a, b) => b.capturedAt - a.capturedAt);
        const promoted = dependents[0];
        if (!promoted) {
          store.delete(d.messageId);
          evictedIds.push(d.messageId);
          continue;
        }
        const promotedRow: SnapshotRow = {
          messageId: promoted.messageId,
          bytes: d.bytes,
          capturedAt: promoted.capturedAt,
        };
        store.put(promotedRow);
        for (const alias of dependents) {
          if (alias.messageId === promoted.messageId) continue;
          const rewritten: SnapshotAliasRow = {
            messageId: alias.messageId,
            aliasOf: promoted.messageId,
            capturedAt: alias.capturedAt,
          };
          store.put(rewritten);
        }
        store.delete(d.messageId);
        evictedIds.push(d.messageId);
      }
    }

    await txDone(tx);
    log.debug("addin.snapshot.captured", {
      messageId,
      bytes: bytes.byteLength,
      durationMs: Date.now() - startedAt,
    });
    return { evictedIds, deduped: false };
  } finally {
    db.close();
  }
};

export const loadSnapshot = async (
  messageId: string,
): Promise<SnapshotRow | null> => {
  const db = await openDb();
  try {
    const tx = db.transaction(STORE, "readonly");
    const store = tx.objectStore(STORE);
    const seen = new Set<string>();
    let currentId = messageId;
    const originalCapturedAt = ((): number | null => null)();
    let firstCapturedAt: number | null = originalCapturedAt;
    for (let hops = 0; hops < 8; hops++) {
      if (seen.has(currentId)) return null;
      seen.add(currentId);
      const row: unknown = await reqDone(store.get(currentId));
      if (typeof row !== "object" || row === null) return null;
      if (isAliasRow(row)) {
        if (firstCapturedAt === null) firstCapturedAt = row.capturedAt;
        currentId = row.aliasOf;
        continue;
      }
      let id: string | null = null;
      let bytes: Uint8Array | null = null;
      let capturedAt: number | null = null;
      for (const [k, v] of Object.entries(row)) {
        if (k === "messageId" && typeof v === "string") id = v;
        if (k === "bytes" && v instanceof Uint8Array) bytes = v;
        if (k === "capturedAt" && typeof v === "number") capturedAt = v;
      }
      if (id === null || bytes === null || capturedAt === null) return null;
      return {
        messageId,
        bytes,
        capturedAt: firstCapturedAt ?? capturedAt,
      };
    }
    return null;
  } finally {
    db.close();
  }
};

export const deleteSnapshot = async (messageId: string): Promise<void> => {
  const db = await openDb();
  try {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).delete(messageId);
    await txDone(tx);
  } finally {
    db.close();
  }
};

// Clear all snapshots — chat history is memory-only, so on app boot every
// stored snapshot is orphaned (no message id to map it to).
export const clearAllSnapshots = async (): Promise<void> => {
  const db = await openDb();
  try {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).clear();
    await txDone(tx);
  } finally {
    db.close();
  }
};

// Pull the active presentation's full .pptx bytes via Office.js. Lossless —
// these are the exact zip bytes PowerPoint would write to disk.
export const captureDeckBytes = async (
  onProgress?: (loaded: number, total: number) => void,
): Promise<Uint8Array> => {
  const SLICE_SIZE = 4 * 1024 * 1024;
  const file = await new Promise<Office.File>((resolve, reject) => {
    Office.context.document.getFileAsync(
      Office.FileType.Compressed,
      { sliceSize: SLICE_SIZE },
      (result) => {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          reject(
            new Error(result.error?.message ?? "getFileAsync failed"),
          );
          return;
        }
        resolve(result.value);
      },
    );
  });

  const sliceCount = file.sliceCount;
  const totalBytes = file.size;
  const slices: Uint8Array[] = [];
  let loaded = 0;
  onProgress?.(0, totalBytes);
  try {
    for (let i = 0; i < sliceCount; i++) {
      const slice = await new Promise<Office.Slice>((resolve, reject) => {
        file.getSliceAsync(i, (result) => {
          if (result.status !== Office.AsyncResultStatus.Succeeded) {
            reject(
              new Error(result.error?.message ?? "getSliceAsync failed"),
            );
            return;
          }
          resolve(result.value);
        });
      });
      const data: unknown = slice.data;
      let chunk: Uint8Array;
      if (data instanceof Uint8Array) {
        chunk = data;
      } else if (Array.isArray(data)) {
        chunk = new Uint8Array(data);
      } else if (data instanceof ArrayBuffer) {
        chunk = new Uint8Array(data);
      } else {
        throw new Error("getSliceAsync returned unrecognized slice.data type");
      }
      slices.push(chunk);
      loaded += chunk.byteLength;
      onProgress?.(loaded, totalBytes);
    }
  } finally {
    await new Promise<void>((resolve) => {
      file.closeAsync(() => {
        resolve();
      });
    });
  }

  let total = 0;
  for (const s of slices) total += s.byteLength;
  const out = new Uint8Array(total);
  let offset = 0;
  for (const s of slices) {
    out.set(s, offset);
    offset += s.byteLength;
  }
  return out;
};

// Convert raw bytes to base64 in chunks (avoid stack blow-up on big buffers).
export const bytesToBase64 = (bytes: Uint8Array): string => {
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return globalThis.btoa(binary);
};

// Replace the live deck contents with the snapshot, in-place — no file close,
// same SharePoint/OneDrive item id. Inserts snapshot slides at the end, then
// deletes the original slides. PowerPoint shows the change as one visual
// update inside a single PowerPoint.run.
export const restoreSnapshot = async (bytes: Uint8Array, messageId?: string): Promise<void> => {
  const startedAt = Date.now();
  const base64 = bytesToBase64(bytes);
  try {
    await executeOps([{ op: "replaceDeckWithBase64", base64 }]);
  } catch (err) {
    log.error("addin.snapshot.error", { op: "restoreSnapshot", error: err instanceof Error ? err.message : String(err) });
    throw err;
  }
  log.info("addin.snapshot.restored", {
    messageId: messageId ?? null,
    durationMs: Date.now() - startedAt,
  });
};

export type SnapshotListItem = {
  messageId: string;
  capturedAt: number;
  sizeBytes: number;
};

export const listSnapshots = async (): Promise<SnapshotListItem[]> => {
  const db = await openDb();
  try {
    const tx = db.transaction(STORE, "readonly");
    const store = tx.objectStore(STORE);
    const out: SnapshotListItem[] = [];
    await new Promise<void>((resolve, reject) => {
      const cursorReq = store.openCursor();
      cursorReq.onerror = () => {
        reject(cursorReq.error ?? new Error("cursor failed"));
      };
      cursorReq.onsuccess = () => {
        const cursor = cursorReq.result;
        if (!cursor) {
          resolve();
          return;
        }
        const v: unknown = cursor.value;
        if (typeof v === "object" && v !== null) {
          let id: string | null = null;
          let at: number | null = null;
          let size: number | null = null;
          for (const [k, val] of Object.entries(v)) {
            if (k === "messageId" && typeof val === "string") id = val;
            if (k === "capturedAt" && typeof val === "number") at = val;
            if (k === "bytes" && val instanceof Uint8Array) size = val.byteLength;
          }
          if (id !== null && at !== null && size !== null) {
            out.push({ messageId: id, capturedAt: at, sizeBytes: size });
          }
        }
        cursor.continue();
      };
    });
    out.sort((a, b) => b.capturedAt - a.capturedAt);
    return out;
  } finally {
    db.close();
  }
};

export const restoreSingleSlide = async (
  snapshotBytes: Uint8Array,
  snapshotSlideIndex: number,
  currentSlideIndex: number,
): Promise<void> => {
  const parsed = await parseDeck(snapshotBytes);
  const target = parsed[snapshotSlideIndex];
  if (!target) {
    throw new Error(`Snapshot has no slide at index ${snapshotSlideIndex.toString()}`);
  }
  try {
    await executeOps([
      {
        op: "replaceSlideFromBase64",
        base64: bytesToBase64(snapshotBytes),
        sourceSlideId: target.slideId,
        currentSlideIndex,
      },
    ]);
  } catch (err) {
    log.error("addin.snapshot.error", { op: "restoreSingleSlide", error: err instanceof Error ? err.message : String(err) });
    throw err;
  }
};
